// Applies the resolved interface locale to the running i18next instance.
//
// Sources come from the auth store rather than a fresh `GET_ME`, for two
// reasons: the store is persisted to localStorage and rehydrates
// synchronously, so a returning operator's locale is known on the first
// render; and the provider sits above the authentication gate, where a `me`
// query would simply fail.
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { I18nextProvider } from "react-i18next";
import useAuthStore from "../stores/authStore";
import i18n, { loadLocale } from "./index";
import {
    directionForInterfaceLanguage,
    getBrowserLanguages,
    resolveInterfaceLanguage,
} from "../utils/interfaceLanguages";

export function LocaleProvider({ children }: { children: ReactNode }) {
    const user = useAuthStore((s) => s.user);
    const currentOrganization = useAuthStore((s) => s.currentOrganization);

    const organizationDefault = useMemo(() => {
        const orgs = user?.organizations ?? [];
        const active = orgs.find((org) => org.id === currentOrganization) ?? orgs[0];
        return active?.settings?.interfaceLanguage ?? null;
    }, [user?.organizations, currentOrganization]);

    const language = useMemo(
        () =>
            resolveInterfaceLanguage({
                userPreference: user?.preferences?.interfaceLanguage ?? null,
                organizationDefault,
                browserLanguages: getBrowserLanguages(),
            }),
        [user?.preferences?.interfaceLanguage, organizationDefault],
    );

    // Gates the first paint only while a *non-English* catalogue is in flight.
    // English is bundled, so the common path never waits.
    const [ready, setReady] = useState(() => language === i18n.language);

    useEffect(() => {
        let cancelled = false;

        const apply = async () => {
            await loadLocale(language);
            if (cancelled) return;
            if (i18n.language !== language) await i18n.changeLanguage(language);
            if (!cancelled) setReady(true);
        };

        void apply();
        return () => {
            cancelled = true;
        };
    }, [language]);

    useEffect(() => {
        const root = document.documentElement;
        root.lang = language;
        root.dir = directionForInterfaceLanguage(language);
    }, [language]);

    if (!ready) return null;

    return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}

export default LocaleProvider;
